import { AxiosError, AxiosResponse } from "axios";
import React, { useContext, useEffect, useState } from "react";
import Router from "next/router";
import PrettyButton from "@components/common/PrettyButton";
import { ApiApi } from "@api";
import { useForm } from "react-hook-form";
import { AuthContext } from "@storage/AuthProvider";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { COMPANY_PLACEHOLDER_TEXT } from "@helpers/types";
import {
  Button,
  Typography,
  Stack,
  TextField,
  Box,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { InvitedText } from "@components/onboarding/signup/InvitedText";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import { useInvitation } from "@helpers/queries/invitations";
import { gtagReportConversion } from "@helpers/AnalyticsHelper";

interface Props {
  inviteToken?: string;
}

interface RegisterResponse {
  key: string;
}

type RegisterValues = {
  name: string;
  companyName: string;
  email: string;
  password: string;
};

const schema = Yup.object({
  name: Yup.string().required("Name is required"),
  companyName: Yup.string().required("Company name is required"),
  email: Yup.string().email().required("Email is required"),
  password: Yup.string()
    .required()
    .test(
      "len",
      "Must be at least 6 characters",
      (val?: string) => val != null && val.length >= 6,
    ),
}).required();

const SignupForm: React.FC<Props> = ({ inviteToken }) => {
  const [showSecrets, setShowSecrets] = useState(false);
  const { setAccessToken, user } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const api = new ApiApi();

  const { data: invitation, isLoading: isInvitationLoading } = useInvitation(
    { token: inviteToken ?? "" },
    { enabled: inviteToken == null },
  );

  const defaultValues = {
    name: invitation
      ? `${String(invitation.first_name)} ${String(invitation.last_name)}`
      : "",
    companyName: invitation?.company?.name,
    email: invitation?.email,
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RegisterValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues,
  });

  const onSubmit = handleSubmit(async (formData: RegisterValues) => {
    try {
      setIsLoading(true);
      const [firstName, ...lastName] = formData.name?.split(" ") ?? [];

      const response = await api.apiV1RegistrationCreate({
        first_name: firstName,
        last_name: lastName?.join(" "),
        email: formData.email,
        password1: formData.password,
        password2: formData.password,
        company: inviteToken
          ? undefined
          : {
              name: formData.companyName,
              address_1: COMPANY_PLACEHOLDER_TEXT,
              city: COMPANY_PLACEHOLDER_TEXT,
              post_code: COMPANY_PLACEHOLDER_TEXT,
              country: COMPANY_PLACEHOLDER_TEXT,
            },
        token: inviteToken,
      });
      const responseData = response.data as unknown as RegisterResponse;
      setAccessToken(responseData.key);
      gtagReportConversion(responseData.key);
    } catch (e) {
      // TODO this will probably need its own helper function
      const fieldErrors = (e as AxiosError[])?.[0]?.response?.data as {
        first_name?: string[];
        last_name?: string[];
        email?: string[];
        password1?: string[];
      };

      // In this case, it is not an AxiosError but a response array
      const tokenError = (e as AxiosResponse[])?.[0]?.data?.token;
      if (tokenError && tokenError.length > 0) {
        toast.error(tokenError.join(" "));
      }

      if (
        fieldErrors?.first_name?.length != null ||
        fieldErrors?.last_name?.length != null
      ) {
        setError("name", { type: "custom", message: "Enter your full name" });
      }
      if (fieldErrors?.email?.length != null) {
        const emailErrorMessage = fieldErrors.email.join(" ");
        setError("email", {
          type: "custom",
          message: emailErrorMessage.includes("registered")
            ? "This email is already registered. Are you trying to log in?"
            : emailErrorMessage,
        });
      }
      if (fieldErrors?.password1?.length != null) {
        setError("password", {
          type: "custom",
          message: fieldErrors?.password1?.join(" "),
        });
      }
    } finally {
      setIsLoading(false);
    }
  });

  useEffect(() => {
    // Once the user has signed up and is authenticated, we can move on
    // from here.
    if (user?.companyRole) {
      if (inviteToken) {
        Router.push("/dashboard");
      } else {
        Router.push("/account/new/project-questionnaire");
      }
    }
  }, [user?.companyRole, inviteToken]);

  return (
    <Stack spacing={[2, 3]} sx={{ maxWidth: "35rem" }}>
      <Typography variant="h1" fontWeight={800} color="color.blue.dark">
        Sign up
      </Typography>

      {inviteToken && (
        <InvitedText fontSize={20} maxWidth={400} inviteToken={inviteToken} />
      )}
      {!inviteToken && (
        <Typography
          variant="body1"
          color="color.blue.dark"
          sx={{ fontSize: "1.05rem" }}
        >
          You&apos;re <em>this close</em> to get your project tested end-to-end!
          <br />
          Start by signing up here:
        </Typography>
      )}

      <form onSubmit={onSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Your full name"
            {...register("name")}
            error={errors.name != null}
            helperText={errors.name?.message}
            sx={{ background: "white", borderRadius: 1 }}
          />

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Company name"
            {...register("companyName")}
            disabled={!!inviteToken}
            error={errors.companyName != null}
            helperText={errors.companyName?.message}
            sx={{ background: "white", borderRadius: 1 }}
          />

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type="email"
            placeholder="Enter email"
            disabled={!!inviteToken}
            {...register("email")}
            error={errors.email != null}
            helperText={errors.email?.message}
            sx={{ background: "white", borderRadius: 1 }}
          />

          <TextField
            fullWidth
            variant="outlined"
            size="small"
            type={showSecrets ? "text" : "password"}
            autoComplete="off"
            placeholder="8 characters or more"
            {...register("password")}
            error={errors.password != null}
            helperText={errors.password?.message}
            sx={{ background: "white", borderRadius: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => {
                      setShowSecrets(!showSecrets);
                    }}
                  >
                    {showSecrets ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </Button>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            sx={{ width: "fit-content" }}
            control={
              <Checkbox
                checked={termsChecked}
                onChange={() => setTermsChecked((p) => !p)}
              />
            }
            label={
              <Typography>
                I agree to and acknowledge to have read the{" "}
                <a href="/terms/general" target="_blank">
                  Terms &amp; Conditions.
                </a>
              </Typography>
            }
          />
          <Box>
            <PrettyButton
              buttonFunction="submit"
              title="Sign up"
              isLoading={isLoading || isInvitationLoading}
              disabled={!termsChecked}
              onClick={onSubmit}
            />
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};

export default SignupForm;
